import type { Session } from "@training/shared";
import type { Entry, SetFigures } from "./session.ts";
import { dropLoadOf, format, formatDate, hasSides, myoSetLabel, sideOf, targetLabel } from "./session.ts";
import { describeTarget } from "./circuit.ts";

/**
 * The session as plain text, every set spelled out, to paste into another
 * tracker's assistant. Whoop detects the workout but cannot be told its sets
 * through its API, and screenshots of the app read badly: here nothing is
 * left to guess — no "8/7", no arrow, no unit dropped.
 */
export function sessionRecap(session: Session, entries: Entry[]): string {
  const lines = [`Séance de musculation « ${session.name} », ${formatDate(session.date)}${timeSpan(session)}.`];

  let number = 0;
  for (const entry of entries) {
    const sets = setLines(entry);
    if (sets.length === 0) continue;
    number += 1;
    lines.push("", `${number}. ${heading(entry)}`, ...sets.map((line) => `- ${line}`));
  }
  return lines.join("\n");
}

function timeSpan(session: Session): string {
  if (session.startedAt && session.finishedAt) {
    return `, de ${clock(session.startedAt)} à ${clock(session.finishedAt)}`;
  }
  return session.startedAt ? `, commencée à ${clock(session.startedAt)}` : "";
}

/** "18:05", on the device's clock: the hour Whoop saw the workout at. */
function clock(iso: string): string {
  const at = new Date(iso);
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

function heading(entry: Entry): string {
  const notes = [entry.method, entry.circuitGroup ? `circuit « ${entry.circuitGroup} »` : null].filter(Boolean);
  return notes.length > 0 ? `${entry.exercise.name} (${notes.join(", ")})` : entry.exercise.name;
}

function setLines(entry: Entry): string[] {
  if (entry.sets.length > 0) {
    return entry.sets.map((set) => {
      const label = myoSetLabel(entry, set.setIndex) ?? `Série ${set.setIndex}`;
      const warmup = set.isWarmup ? " (échauffement)" : "";
      const figures = set.durationSeconds !== null ? hold(set.weightKg, set.durationSeconds) : lift(set);
      return `${label}${warmup} : ${figures}`;
    });
  }
  // Done outside the app: the sheet kept the loads, never the reps.
  if (entry.sheetSets.length > 0) {
    return entry.sheetSets.map((set) => `Série ${set.setIndex} : ${load(set.weightKg)}, reps non notées`);
  }
  // A circuit ticked off as a whole: what was asked is all there is to say.
  if (entry.doneAt) {
    const asked = entry.targetSets ? targetLabel(entry) : describeTarget(entry);
    return [asked ? `fait, séries non détaillées (prévu : ${asked})` : "fait, séries non détaillées"];
  }
  return [];
}

function hold(weightKg: number, seconds: number): string {
  return weightKg > 0 ? `${load(weightKg)}, tenu ${seconds} s` : `tenu ${seconds} s`;
}

function lift(set: SetFigures): string {
  if (!hasSides(set)) return side(set);
  const left = side(sideOf(set, "left"));
  const right = side(sideOf(set, "right"));
  return left === right ? `${left} par côté` : `côté gauche ${left} ; côté droit ${right}`;
}

/** One side of a set, or a set with no sides: its load, its reps, its drop. */
function side(set: SetFigures): string {
  const first = `${load(set.weightKg)} × ${reps(set.reps)}`;
  const drop = dropLoadOf(set);
  if (drop === null) return first;
  // A drop logged before its own reps were: one count for both loads.
  if (set.dropReps == null) return `${load(set.weightKg)} puis ${load(drop)} sans pause × ${reps(set.reps)} au total`;
  return `${first}, puis dégressive sans pause à ${load(drop)} × ${reps(set.dropReps)}`;
}

function load(kg: number): string {
  return kg > 0 ? `${format(kg)} kg` : "poids du corps";
}

function reps(count: number | null): string {
  if (count === null) return "? reps";
  return `${count} rep${count > 1 ? "s" : ""}`;
}
